import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { policiesService } from "api/services/policies.service";
import ContentHeader from "components/Layout/ContentHeader";
import PolicyFormModal from "components/product-group/PolicyFormModal";
import Button from "components/UI/Button";

import type { AfterServicePolicyCreateRequest, AfterServicePolicyFull, ReturnExchangePolicyCreateRequest, ReturnExchangePolicyFull, ShippingPolicyCreateRequest, ShippingPolicyFull, ServiceCenter } from "api/types/policies.types";
import type { PolicyFormPayload, PolicyFormType } from "components/product-group/PolicyFormModal";
import "pages/AdminPage.scss";
import "./PolicyDetail.scss";

type PolicyType = PolicyFormType;

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

const TITLE_MAP: Record<PolicyType, string> = {
  shipping: "배송 정책",
  "return-exchange": "반품/교환 정책",
  "after-service": "A/S 정책",
};

const PolicyDetail: React.FC = () => {
  const navigate = useNavigate();
  const { type, id } = useParams<{ type: PolicyType; id: string }>();
  const policyId = Number(id);

  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const [shipping, setShipping] = useState<ShippingPolicyFull | null>(null);
  const [returnExchange, setReturnExchange] = useState<ReturnExchangePolicyFull | null>(null);
  const [afterService, setAfterService] = useState<AfterServicePolicyFull | null>(null);

  // 수정 모달에 넘길 초기값
  const [initialShipping, setInitialShipping] = useState<Partial<ShippingPolicyCreateRequest>>({});
  const [initialReturn, setInitialReturn] = useState<Partial<ReturnExchangePolicyCreateRequest>>({});
  const [initialAs, setInitialAs] = useState<Partial<AfterServicePolicyCreateRequest>>({});

  const fetchPolicy = async () => {
    setLoading(true);
    try {
      const res = await policiesService.getOptions();
      const data = res.data;
      if (type === "shipping") {
        const found = data.shipping_policies.find((p) => p.id === policyId) ?? null;
        setShipping(found);
        if (found) {
          setInitialShipping({
            name: found.name,
            shipping_enabled: found.shipping_enabled,
            method: found.method ?? "",
            courier: found.courier ?? "",
            bundle_shipping: found.bundle_shipping,
            base_fee: found.base_fee,
            free_threshold: found.free_threshold ?? "",
            pay_type: found.pay_type,
          });
        }
      } else if (type === "return-exchange") {
        const found = data.return_exchange_policies.find((p) => p.id === policyId) ?? null;
        setReturnExchange(found);
        if (found) {
          setInitialReturn({
            name: found.name,
            carrier: found.carrier ?? "",
            return_fee_oneway: found.return_fee_oneway,
            exchange_fee_round: found.exchange_fee_round,
            address: found.address ?? "",
          });
        }
      } else if (type === "after-service") {
        const found = data.after_service_policies.find((p) => p.id === policyId) ?? null;
        setAfterService(found);
        if (found) {
          setInitialAs({
            name: found.name,
            phone: found.phone ?? "",
            guide: found.guide ?? "",
            warranty_period: found.warranty_period ?? "",
          });
        }
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPolicy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, id]);

  const handleDelete = async () => {
    if (!window.confirm("이 정책을 삭제하시겠습니까?")) return;
    try {
      if (type === "shipping") await policiesService.deleteShipping(policyId);
      else if (type === "return-exchange") await policiesService.deleteReturnExchange(policyId);
      else await policiesService.deleteAfterService(policyId);
      navigate("/policy-management");
    } catch {
      // ignore
    }
  };

  const handleSave = async (payload: PolicyFormPayload) => {
    try {
      if (type === "shipping") {
        await policiesService.updateShipping(policyId, payload as ShippingPolicyCreateRequest);
      } else if (type === "return-exchange") {
        await policiesService.updateReturnExchange(policyId, payload as ReturnExchangePolicyCreateRequest);
      } else {
        await policiesService.updateAfterService(policyId, payload as AfterServicePolicyCreateRequest);
      }
      setShowEditModal(false);
      fetchPolicy();
    } catch {
      // ignore
    }
  };

  const currentName = shipping?.name ?? returnExchange?.name ?? afterService?.name ?? "-";

  if (loading) {
    return (
      <div className="admin-page">
        <div className="policy-detail__loading">불러오는 중...</div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <ContentHeader
        title={`${TITLE_MAP[type ?? "shipping"]} 상세`}
        description={currentName}
        actions={[
          <Button key="back" variant="secondary" onClick={() => navigate("/policy-management")}>
            목록으로
          </Button>,
          <Button key="edit" variant="primary" onClick={() => setShowEditModal(true)}>
            수정
          </Button>,
          <Button key="delete" variant="danger" onClick={handleDelete}>
            삭제
          </Button>,
        ]}
      />

      <div className="admin-page__card policy-detail">
        {/* 배송 정책 상세 */}
        {type === "shipping" && shipping && (
          <>
            <div className="admin-page__section">
              <div className="admin-page__section-title">기본 정보</div>
              <div className="policy-detail__grid">
                <DetailRow label="정책명" value={shipping.name} />
                <DetailRow label="라벨" value={shipping.label ?? "-"} />
                <DetailRow label="배송 사용" value={shipping.shipping_enabled ? "사용" : "미사용"} />
                <DetailRow label="배송 방법" value={shipping.method ?? "-"} />
                <DetailRow label="택배사" value={shipping.courier ?? "-"} />
                <DetailRow label="묶음 배송" value={shipping.bundle_shipping ? "가능" : "불가"} />
                <DetailRow label="기본 배송비" value={`${Number(shipping.base_fee).toLocaleString()}원`} />
                <DetailRow label="무료 배송 기준" value={shipping.free_threshold ? `${Number(shipping.free_threshold).toLocaleString()}원 이상` : "없음"} />
                <DetailRow label="결제 방식" value={shipping.pay_type === "prepaid" ? "선불" : shipping.pay_type === "cod" ? "착불" : "혼합"} />
                <DetailRow label="등록일" value={formatDate(shipping.created_at)} />
                <DetailRow label="수정일" value={formatDate(shipping.updated_at)} />
              </div>
            </div>

            {shipping.regional_info && (
              <div className="admin-page__section">
                <div className="admin-page__section-title">도서산간 추가 배송비</div>
                {shipping.regional_info.description && <p className="policy-detail__region-desc">{shipping.regional_info.description}</p>}
                <div className="policy-detail__region-table">
                  <table>
                    <thead>
                      <tr>
                        <th>추가 배송비</th>
                        <th>해당 지역</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shipping.regional_info.zones_summary.map((zone, idx) => (
                        <tr key={idx}>
                          <td className="policy-detail__region-fee">{zone.extra_fee.toLocaleString()}원</td>
                          <td>
                            <ul className="policy-detail__region-list">
                              {zone.regions.map((r, i) => (
                                <li key={i}>{r}</li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* 반품/교환 정책 상세 */}
        {type === "return-exchange" && returnExchange && (
          <>
            <div className="admin-page__section">
              <div className="admin-page__section-title">기본 정보</div>
              <div className="policy-detail__grid">
                <DetailRow label="정책명" value={returnExchange.name} />
                <DetailRow label="라벨" value={returnExchange.label ?? "-"} />
                <DetailRow label="택배사" value={returnExchange.carrier ?? "-"} />
                <DetailRow label="반품 배송비 (편도)" value={`${Number(returnExchange.return_fee_oneway).toLocaleString()}원`} />
                <DetailRow label="교환 배송비 (왕복)" value={`${Number(returnExchange.exchange_fee_round).toLocaleString()}원`} />
                <DetailRow label="회수지 주소" value={returnExchange.address ?? "-"} />
                <DetailRow label="등록일" value={formatDate(returnExchange.created_at)} />
                <DetailRow label="수정일" value={formatDate(returnExchange.updated_at)} />
              </div>
            </div>

            {returnExchange.responsibility_rules && Object.keys(returnExchange.responsibility_rules).length > 0 && (
              <div className="admin-page__section">
                <div className="admin-page__section-title">책임 부담 정책</div>
                <div className="policy-detail__resp-table">
                  <table>
                    <thead>
                      <tr>
                        <th>사유</th>
                        <th>책임자</th>
                        <th>배송비</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(returnExchange.responsibility_rules).map(([reason, rule]) => (
                        <tr key={reason}>
                          <td>{reason}</td>
                          <td>{rule["책임자"]}</td>
                          <td>{rule["배송비"]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* A/S 정책 상세 */}
        {type === "after-service" && afterService && (
          <>
            <div className="admin-page__section">
              <div className="admin-page__section-title">기본 정보</div>
              <div className="policy-detail__grid">
                <DetailRow label="정책명" value={afterService.name} />
                <DetailRow label="라벨" value={afterService.label ?? "-"} />
                <DetailRow label="A/S 전화번호" value={afterService.phone ?? "-"} />
                <DetailRow label="보증 기간" value={afterService.warranty_period ?? "-"} />
                <DetailRow label="등록일" value={formatDate(afterService.created_at)} />
                <DetailRow label="수정일" value={formatDate(afterService.updated_at)} />
              </div>
            </div>

            {afterService.guide && (
              <div className="admin-page__section">
                <div className="admin-page__section-title">A/S 안내</div>
                <p className="policy-detail__guide">{afterService.guide}</p>
              </div>
            )}

            {afterService.service_centers && (
              <div className="admin-page__section">
                <div className="admin-page__section-title">서비스 센터</div>
                {(() => {
                  const centers = Array.isArray(afterService.service_centers) ? afterService.service_centers : [afterService.service_centers as ServiceCenter];
                  return (
                    <div className="policy-detail__centers">
                      {centers.map((c, i) => (
                        <div key={i} className="policy-detail__center-card">
                          <div className="policy-detail__center-name">{c.name}</div>
                          <div className="policy-detail__center-row">
                            <span>주소</span>
                            <span>{c.address}</span>
                          </div>
                          <div className="policy-detail__center-row">
                            <span>전화</span>
                            <span>{c.tel}</span>
                          </div>
                          <div className="policy-detail__center-row">
                            <span>운영시간</span>
                            <span>{c.hours}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}
          </>
        )}
      </div>

      <PolicyFormModal open={showEditModal} type={type ?? "shipping"} submitLabel="저장" onClose={() => setShowEditModal(false)} onSubmit={handleSave} initialShipping={initialShipping} initialReturnExchange={initialReturn} initialAfterService={initialAs} />
    </div>
  );
};

/* ── 공통 DetailRow 컴포넌트 ── */
const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="policy-detail__row">
    <span className="policy-detail__label">{label}</span>
    <span className="policy-detail__value">{value}</span>
  </div>
);

export default PolicyDetail;
